import { CommonValidator } from '../utils/commonValidator';
import { EncoderToolType, EncodingOperation } from '../types/EncodingOperation';
import { GZipConfiguration, GZipMode } from '../types/Compression';
import * as fflate from 'fflate';

export class GZipService {
  /**
   * Creates a GZip configuration
   */
  static createConfiguration(
    input: string | Uint8Array,
    mode: GZipMode = GZipMode.COMPRESS,
    options: {
      level?: number;
      filename?: string;
      timestamp?: number;
      comment?: string;
      outputFormat?: 'base64' | 'hex' | 'uint8array';
    } = {}
  ): GZipConfiguration {
    return {
      input,
      mode,
      level: options.level ?? 6,
      filename: options.filename ?? undefined,
      timestamp: options.timestamp ?? Math.floor(Date.now() / 1000),
      comment: options.comment ?? undefined,
      outputFormat: options.outputFormat ?? 'base64'
    };
  }

  /**
   * Validates GZip configuration
   */
  static validateConfiguration(config: GZipConfiguration): void {
    if (!config.input) {
      throw new Error('Input data is required');
    }

    if (!Object.values(GZipMode).includes(config.mode)) {
      throw new Error('Invalid mode specified');
    }

    if (config.timestamp !== undefined && (typeof config.timestamp !== 'number' || config.timestamp < 0)) {
      throw new Error('Timestamp must be a positive number');
    }

    if (typeof config.level !== 'number' || config.level < 0 || config.level > 9) {
      throw new Error('Compression level must be between 0 and 9');
    }

    if (config.comment && config.comment.length > 65535) {
      throw new Error('Comment too long (max 65535 characters)');
    }

    if (!['base64', 'hex', 'uint8array'].includes(config.outputFormat || '')) {
      throw new Error('Invalid output format');
    }
  }

  /**
   * Compresses data using GZip
   */
  static async compress(config: GZipConfiguration): Promise<EncodingOperation> {
    try {
      this.validateConfiguration(config);

      const inputData = this.prepareInputData(config.input, GZipMode.COMPRESS);
      const validation = CommonValidator.validateText(
        typeof config.input === 'string' ? config.input : new TextDecoder().decode(config.input),
        CommonValidator.getValidationRules(EncoderToolType.GZIP_COMPRESSION)
      );

      if (!validation.isValid) {
          return {
          id: `gzip-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          status: 'error',
          errorMessage: validation.errors?.join(', ') || 'Invalid input data',
          operationType: 'encode',
          toolType: EncoderToolType.GZIP_COMPRESSION,
          timestamp: new Date(),
          inputData: typeof config.input === 'string' ? config.input : 'binary data',
          outputData: null
        };
      }

      // Create GZip options - ensure deterministic output
      const gzipOptions = {
        // GZip specific options
        to: 'gzip',
        level: config.level as any,
        // fflate specific options for deterministic output
        mtime: 0, // Fixed modification time
        filename: undefined, // No filename
        comment: undefined // No comment
      };

      // Compress the data
      const compressedData = fflate.gzipSync(inputData, gzipOptions);
      const outputData = this.formatOutput(compressedData, config.outputFormat);

      return {
        id: `gzip-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        status: 'success',
        operationType: 'encode',
        toolType: EncoderToolType.GZIP_COMPRESSION,
        timestamp: new Date(),
        inputData: typeof config.input === 'string' ? config.input : 'binary data',
        outputData: outputData,
        metadata: {
          originalSize: inputData.length,
          compressedSize: compressedData.length,
          compressionRatio: inputData.length > 0 ? compressedData.length / inputData.length : 0,
          compressionLevel: config.level,
          filename: config.filename,
          timestamp: config.timestamp,
          comment: config.comment,
          outputFormat: config.outputFormat
        }
      };

    } catch (error) {
      return {
        id: `gzip-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Unknown error occurred',
        operationType: 'encode',
        toolType: EncoderToolType.GZIP_COMPRESSION,
        timestamp: new Date(),
        inputData: typeof config.input === 'string' ? config.input : 'binary data',
        outputData: null
      };
    }
  }

  /**
   * Decompresses GZip data
   */
  static async decompress(config: GZipConfiguration): Promise<EncodingOperation> {
    try {
      this.validateConfiguration(config);

      const inputData = this.prepareInputData(config.input, GZipMode.DECOMPRESS);

      // Decompress the data
      let decompressedData: Uint8Array;
      try {
        decompressedData = fflate.gunzipSync(inputData);
      } catch (decompressError) {
        throw new Error(`Failed to decompress data: ${decompressError instanceof Error ? decompressError.message : 'Unknown error'}`);
      }

      // Format output based on input type
      let outputData: string | Uint8Array;
      if (typeof config.input === 'string') {
        outputData = new TextDecoder().decode(decompressedData);
      } else {
        outputData = decompressedData;
      }

      return {
        id: `gzip-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        status: 'success',
        operationType: 'decode',
        toolType: EncoderToolType.GZIP_COMPRESSION,
        timestamp: new Date(),
        inputData: typeof config.input === 'string' ? config.input : 'binary data',
        outputData: outputData,
        metadata: {
          originalSize: inputData.length,
          decompressedSize: decompressedData.length,
          compressionRatio: decompressedData.length > 0 ? inputData.length / decompressedData.length : 0,
          outputFormat: config.outputFormat
        }
      };

    } catch (error) {
      return {
        id: `gzip-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Unknown error occurred',
        operationType: 'decode',
        toolType: EncoderToolType.GZIP_COMPRESSION,
        timestamp: new Date(),
        inputData: typeof config.input === 'string' ? config.input : 'binary data',
        outputData: null
      };
    }
  }

  /**
   * Main processing function
   */
  static async process(config: GZipConfiguration): Promise<EncodingOperation> {
    if (config.mode === GZipMode.COMPRESS) {
      return this.compress(config);
    } else if (config.mode === GZipMode.DECOMPRESS) {
      return this.decompress(config);
    } else {
      throw new Error('Unsupported mode');
    }
  }

  /**
   * Prepares input data for processing
   */
  private static prepareInputData(input: string | Uint8Array, mode: GZipMode): Uint8Array {
    if (typeof input === 'string') {
      if (mode === GZipMode.DECOMPRESS) {
        // For decompression, detect different input formats
        if (input.startsWith('data:application/gzip;base64,')) {
          // Remove data URL prefix
          const base64Data = input.split(',')[1];
          return this.base64ToUint8Array(base64Data);
        } else if (input.startsWith('H4sI')) {
          // GZip magic bytes in Base64 format
          return this.base64ToUint8Array(input);
        } else if (this.isHex(input)) {
          return this.hexToUint8Array(input);
        } else if (this.isBase64(input)) {
          // Valid Base64 (but not starting with H4sI, so probably not GZip)
          return this.base64ToUint8Array(input);
        } else {
          // Regular string - treat as text to decompress (probably error case)
          return new TextEncoder().encode(input);
        }
      } else {
        // For compression, always treat as regular string
        return new TextEncoder().encode(input);
      }
    } else {
      return input;
    }
  }

  /**
   * Formats output data according to requested format
   */
  private static formatOutput(data: Uint8Array, format?: string): string | Uint8Array {
    switch (format) {
      case 'base64':
        return this.uint8ArrayToBase64(data);
      case 'hex':
        return this.uint8ArrayToHex(data);
      case 'uint8array':
        return data;
      default:
        return this.uint8ArrayToBase64(data);
    }
  }

  /**
   * Checks if string is Base64 encoded
   */
  private static isBase64(str: string): boolean {
    try {
      // Check if the string looks like Base64 (only contains Base64 characters and proper padding)
      if (!/^[A-Za-z0-9+/]*={0,2}$/.test(str)) {
        return false;
      }
      // Check if length is a multiple of 4 (Base64 requirement)
      if (str.length % 4 !== 0) {
        return false;
      }
      // For short strings (less than 12 chars), be extra strict
      if (str.length < 12) {
        // Short strings that are all lowercase letters are likely not intended as Base64
        if (/^[a-z]+$/.test(str)) {
          return false;
        }
      }
      // Additional check: try to decode it
      const decoded = atob(str);
      // Check if re-encoding gives the same result
      return btoa(decoded) === str;
    } catch {
      return false;
    }
  }

  /**
   * Checks if string is hex encoded
   */
  private static isHex(str: string): boolean {
    return /^[0-9a-fA-F]+$/.test(str) && str.length % 2 === 0;
  }

  /**
   * Converts Base64 to Uint8Array
   */
  private static base64ToUint8Array(base64: string): Uint8Array {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * Converts Uint8Array to Base64
   */
  private static uint8ArrayToBase64(bytes: Uint8Array): string {
    let binaryString = '';
    for (let i = 0; i < bytes.length; i++) {
      binaryString += String.fromCharCode(bytes[i]);
    }
    return btoa(binaryString);
  }

  /**
   * Converts hex string to Uint8Array
   */
  private static hexToUint8Array(hex: string): Uint8Array {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
    }
    return bytes;
  }

  /**
   * Converts Uint8Array to hex string
   */
  private static uint8ArrayToHex(bytes: Uint8Array): string {
    return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Gets compression statistics
   */

  /**
   * Validates GZip data format
   */

  /**
   * Gets GZip header information
   */

  /**
   * Batch process multiple configurations
   */

  /**
   * Compresses multiple files into a single GZip archive
   */

  /**
   * Creates a simple TAR archive (basic implementation)
   */

  
  /**
   * Gets supported compression levels
   */

  /**
   * Estimates compression ratio for input
   */

  /**
   * Validates compressed data integrity
   */

  /**
   * Clears any cached data (for testing)
   */
  static clearCache(): void {
    // No-op: This service doesn't maintain cache
  }
}